import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Check, Copy } from "lucide-react";
import { cn } from "../lib/cn";

/**
 * Copies a value and flips to a tick for a moment. Used on IDs and references,
 * which operators otherwise select by hand out of a table cell.
 */
export function CopyButton({
  value,
  label = "Copy",
  className,
}: {
  value: string;
  label?: string;
  className?: string;
}) {
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 1_600);
    return () => clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      // Clipboard can be blocked by permissions; failing silently is fine here
      // because the value is still visible and selectable on screen.
    }
  };

  return (
    <button
      type="button"
      onClick={() => void copy()}
      title={copied ? "Copied" : label}
      aria-label={copied ? "Copied" : label}
      className={cn(
        "relative grid size-7 shrink-0 place-items-center rounded-md text-content-subtle",
        "transition-colors hover:bg-surface-sunken hover:text-content",
        "focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-gold-500",
        className,
      )}
    >
      <AnimatePresence initial={false} mode="wait">
        <motion.span
          key={copied ? "done" : "idle"}
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.5, opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="absolute grid place-items-center"
        >
          {copied ? (
            <Check size={13} className="text-success-500" aria-hidden="true" />
          ) : (
            <Copy size={13} aria-hidden="true" />
          )}
        </motion.span>
      </AnimatePresence>
    </button>
  );
}

/** Monospace identifier with a copy affordance. */
export function CopyableId({
  value,
  truncate = true,
  className,
}: {
  value: string;
  /** Clip to one line with an ellipsis. Off shows the whole value, wrapped. */
  truncate?: boolean;
  className?: string;
}) {
  return (
    // `max-w-full` plus `min-w-0` on both the wrapper and the code element is
    // what stops a long value escaping its column. Provider "online references"
    // are sometimes a full portal URL, and without these it ran out past the
    // edge of the card once an open sidebar narrowed the pane.
    <span
      className={cn("inline-flex min-w-0 max-w-full items-start gap-1", className)}
    >
      <code
        title={value}
        className={cn(
          "min-w-0 rounded bg-surface-sunken px-1.5 py-0.5 font-mono text-xs text-content-muted",
          truncate
            ? "max-w-40 truncate"
            : // Identifiers have no spaces to break at, so allow a break
              // anywhere rather than letting one long token force an overflow.
              "break-all",
        )}
      >
        {value}
      </code>
      <CopyButton value={value} label="Copy identifier" className="mt-px" />
    </span>
  );
}
